/**
 * Unified product-event tracker.
 *
 * Card n447gxvy — OBS F1.1 trackEvent helper + commonProps + entryChannel.
 *
 * Contract
 * --------
 * - Único punto de entrada para emitir eventos de producto desde el
 *   frontend. F1.2 (auth events), F2.1 (search), F3.1 (investment plan)
 *   consumen esta firma.
 * - **Consent gate hard**: si `hasAnalyticsConsent()` es `false`, el
 *   evento se descarta silenciosamente. Esta es la regla de privacidad
 *   número uno — nunca emitir sin consentimiento explícito.
 * - Los `common_props` se adjuntan automáticamente (session, hash del
 *   user, entry_channel, UTMs, environment, release). El caller sólo
 *   provee `name` + `props` custom + opcionalmente `userId`/`city`/
 *   `isFirstHome`.
 * - El transporte es Sentry (`captureMessage` con `level=info`,
 *   `tags.category=product`, `contexts.product_event={...}`). F0.7
 *   define retención.
 * - **Tags vs contexts** (card EiDX4uFN): los contexts custom se guardan
 *   pero NO se indexan — no se puede filtrar ni agrupar por ellos en
 *   Discover. Las dimensiones de cardinalidad acotada por las que sí
 *   queremos segmentar (`entry_channel`, `wizard_variant`, steps…) se
 *   promueven además a tag vía `eventTags.ts`, que es donde vive el
 *   criterio de cardinalidad y el guard contra promover identificadores.
 */
import * as Sentry from '@sentry/react';

import { buildCommonProps } from './commonProps';
import { hasAnalyticsConsent } from './consent';
import { buildEventTags } from './eventTags';

/**
 * Contexto opcional del caller — todo esto viaja como `common_props` en el
 * payload. Los agentes downstream que ya conocen el user autenticado
 * pasan `userId`; los flujos anónimos lo omiten.
 */
export interface TrackEventOptions {
  userId?: string;
  city?: string;
  isFirstHome?: boolean;
}

/**
 * Emite un evento de producto.
 *
 * @param name   Nombre canónico del evento (ver plan doc). Se envía como
 *               `tags.event` en Sentry para agrupar.
 * @param props  Payload específico del evento. Se merge con
 *               `common_props`; ante colisión, `props` gana (permitido:
 *               el caller puede sobreescribir `session_id` para eventos
 *               de infra).
 * @param options Contexto de usuario/página. Ver `TrackEventOptions`.
 *
 * @returns Promise que resuelve cuando el mensaje fue entregado al SDK
 *          de Sentry. **No** espera al envío HTTP — Sentry buferea y
 *          transmite asincrónico. Cero rechazos: cualquier error interno
 *          se traga (telemetría no debe romper producto).
 */
export async function trackEvent(
  name: string,
  props: Record<string, unknown> = {},
  options: TrackEventOptions = {},
): Promise<void> {
  // Consent gate — no negociable. Cualquier estado que no sea "granted"
  // explícito se traduce a no-op.
  if (!hasAnalyticsConsent()) return;

  try {
    const common = await buildCommonProps(options);
    const payload = { ...common, ...props };
    Sentry.captureMessage(name, {
      level: 'info',
      tags: {
        // Dimensiones de cardinalidad acotada promovidas a tag — son las
        // únicas por las que Sentry puede filtrar/agrupar en Discover.
        // El criterio vive en `eventTags.ts`. Card EiDX4uFN.
        ...buildEventTags(payload),
        // `category` y `event` son estructurales: se escriben al final para
        // que ninguna dimensión promovida pueda pisarlas.
        category: 'product',
        event: name,
      },
      contexts: {
        // El payload completo sigue acá — esto suma tags, no mueve datos.
        product_event: payload,
      },
    });
  } catch {
    // Telemetría fallando NO debe romper la UX. Silent drop.
  }
}
